/**
 * Side-sheet for editing an existing V2 room.
 *
 * Mutates via useUpdateRoom + useUpsertRoomBankOverride. Same patterns as
 * PropertyEditSheet — sectioned cards, blur-mode inputs, ⌘+Enter save.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Section } from "@/components/admin/v2/primitives/Section";
import { Field } from "@/components/admin/v2/primitives/Field";
import { BilingualTextarea } from "@/components/admin/v2/BilingualTextarea";
import {
  bilingualFromRow,
  bilingualToTranslationsJsonb,
} from "@/lib/v2-translations";
import { parseOptionalNumber, parseRequiredNumber } from "@/lib/v2-numeric";
import { formatSupabaseError } from "@/lib/format-supabase-error";
import { useLanguage } from "@/contexts/LanguageContext";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploader } from "@/components/admin/v2/ImageUploader";
import {
  useApartments,
  useDeleteRoomBankOverride,
  useProperty,
  useUpdateRoom,
  useUpsertRoomBankOverride,
} from "@/hooks/v2/useProperties";
import { extractImageUrls } from "@/lib/v2-images";
import { changedKeynestDetails } from "./room-keynest-details";
import {
  isKeynestDefaultInstructions,
  KEYNEST_DEFAULT_INSTRUCTIONS,
} from "@/lib/key-pickup";
import {
  PICKUP_METHODS,
  ROOM_STATUSES,
  type PickupMethod,
  type Room,
  type RoomBankOverride,
  type RoomStatus,
} from "@/types/property";

// Sentinel for "inherit the pickup method from apartment → property". Radix
// Select can't use an empty-string item value, so we map it to this token.
const INHERIT = "__inherit__";

const PICKUP_METHOD_LABELS: Record<PickupMethod, string> = {
  keynest: "KeyNest partner shop",
  manual: "Manual address (non-KeyNest)",
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  room: Room;
  bankOverride: RoomBankOverride | null;
};

type FormState = {
  title: string;
  price: string;
  /** Base security deposit for the unit (COL-360). */
  depositAmount: string;
  available: boolean;
  status: RoomStatus | "";
  apartment_id: string;
  size_sqm: string;
  floor: string;
  available_from: string;
  /** Default-language (English) description; mirrored to legacy
   *  `description` column on save. */
  descriptionEn: string;
  /** German translation. Empty = not set, omitted from JSONB on save. */
  descriptionDe: string;
  images: string[];
  // Key pickup override (blank = inherit from apartment → property).
  // Location / address reuse the legacy partner-shop columns.
  keynest_partner_shop_name: string;
  keynest_partner_shop_address: string;
  keynest_access_url: string;
  // Per-room KeyNest details (no inheritance). The keynest-checkin sync
  // also writes these, so they are only saved when edited here.
  keynest_pickup_code: string;
  keynest_access_code: string;
  keynest_opening_hours_weekdays: string;
  keynest_opening_hours_saturday: string;
  keynest_key_label: string;
  pickup_method: PickupMethod | typeof INHERIT;
  pickup_instructions: string;
  bank_holder: string;
  bank_iban: string;
  bank_bic: string;
  bank_name: string;
};

function fromRoom(r: Room, bank: RoomBankOverride | null): FormState {
  const desc = bilingualFromRow({
    description: r.description,
    description_translations: r.description_translations,
  });
  return {
    title: r.title ?? "",
    price: r.price != null ? String(r.price) : "",
    depositAmount:
      (r as { deposit_amount?: number | null }).deposit_amount != null
        ? String((r as { deposit_amount?: number | null }).deposit_amount)
        : "",
    available: r.available !== false,
    status: (r.status ?? "") as RoomStatus | "",
    apartment_id: r.apartment_id != null ? String(r.apartment_id) : "",
    size_sqm: r.size_sqm != null ? String(r.size_sqm) : "",
    floor: r.floor ?? "",
    available_from: r.available_from ?? "",
    descriptionEn: desc.en,
    descriptionDe: desc.de,
    images: extractImageUrls(r.images),
    keynest_partner_shop_name: r.keynest_partner_shop_name ?? "",
    keynest_partner_shop_address: r.keynest_partner_shop_address ?? "",
    keynest_access_url: r.keynest_access_url ?? "",
    keynest_pickup_code: r.keynest_pickup_code ?? "",
    keynest_access_code: r.keynest_access_code ?? "",
    keynest_opening_hours_weekdays: r.keynest_opening_hours_weekdays ?? "",
    keynest_opening_hours_saturday: r.keynest_opening_hours_saturday ?? "",
    keynest_key_label: r.keynest_key_label ?? "",
    pickup_method: r.pickup_method ?? INHERIT,
    pickup_instructions: r.pickup_instructions ?? "",
    bank_holder: bank?.holder ?? "",
    bank_iban: bank?.iban ?? "",
    bank_bic: bank?.bic ?? "",
    bank_name: bank?.bank_name ?? "",
  };
}

export function RoomEditSheet({
  open,
  onOpenChange,
  room,
  bankOverride,
}: Props) {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const updateMut = useUpdateRoom();
  const bankUpsertMut = useUpsertRoomBankOverride();
  const bankDeleteMut = useDeleteRoomBankOverride();
  const { data: apartments = [] } = useApartments(room.property_id);
  const { data: property } = useProperty(room.property_id);

  const [form, setForm] = useState<FormState>(() =>
    fromRoom(room, bankOverride)
  );

  // Effective parent key pickup (apartment override → property default) — used
  // to show what each blank field inherits, mirroring the apartment sheet.
  const apartment = apartments.find((a) => a.id === room.apartment_id) ?? null;
  const parentMethod: PickupMethod =
    apartment?.pickup_method ?? property?.pickup_method ?? "keynest";
  const parentLocation =
    apartment?.keynest_pickup_location ??
    property?.keynest_pickup_location ??
    "";
  const parentAddress =
    apartment?.keynest_pickup_address ?? property?.keynest_pickup_address ?? "";
  const parentInstructions =
    apartment?.pickup_instructions ?? property?.pickup_instructions ?? "";
  const parentAccessUrl = property?.keynest_access_url ?? "";
  // The floor a blank field inherits: the apartment picked in the form (not
  // the saved one), so re-assigning the room shows the new apartment's floor.
  const formApartment =
    apartments.find((a) => String(a.id) === form.apartment_id) ?? null;
  const inheritedFloor = (formApartment?.floor ?? "").trim();
  // Method actually in effect for this room (own override, else inherited).
  const effectiveMethod: PickupMethod =
    form.pickup_method === INHERIT ? parentMethod : form.pickup_method;

  // A KeyNest pickup with no instructions anywhere starts with the default
  // text in the box. It is only stored once edited (see handleSubmit), so
  // tenants keep reading it in their own language.
  const [instructionsTouched, setInstructionsTouched] = useState(false);
  const defaultInstructions =
    KEYNEST_DEFAULT_INSTRUCTIONS[language === "de" ? "de" : "en"];
  const instructionsValue =
    !instructionsTouched &&
    effectiveMethod === "keynest" &&
    !form.pickup_instructions &&
    !parentInstructions
      ? defaultInstructions
      : form.pickup_instructions;

  useEffect(() => {
    if (open) {
      setForm(fromRoom(room, bankOverride));
      setInstructionsTouched(false);
    }
  }, [open, room, bankOverride]);

  const isSubmitting =
    updateMut.isPending || bankUpsertMut.isPending || bankDeleteMut.isPending;

  function patch(p: Partial<FormState>) {
    setForm((f) => ({ ...f, ...p }));
  }

  async function handleSubmit() {
    if (!form.title.trim()) {
      toast({
        title: t(
          "admin.apartments.missingRequiredField",
          "Missing required field"
        ),
        description: t(
          "admin.rooms.roomTitleRequired",
          "Room title is required."
        ),
        variant: "destructive",
      });
      return;
    }

    // Validate numerics before opening the mutation. NaN reaching Postgres
    // surfaces a generic "invalid input syntax for type numeric" error
    // far from the actual cause (user typo); bail clearly instead.
    const price = parseRequiredNumber(form.price, 0);
    if (!price.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.rooms.invalidRent",
          'Rent "{{value}}" is not a valid number. Use digits only (e.g. 650).'
        ).replace("{{value}}", price.raw),
        variant: "destructive",
      });
      return;
    }
    const sizeSqm = parseOptionalNumber(form.size_sqm);
    if (!sizeSqm.ok) {
      toast({
        title: t("admin.apartments.invalidNumber", "Invalid number"),
        description: t(
          "admin.rooms.invalidSize",
          'Size "{{value}}" is not a valid number. Use digits only (e.g. 18).'
        ).replace("{{value}}", sizeSqm.raw),
        variant: "destructive",
      });
      return;
    }

    try {
      // The rooms table has images[] (jsonb gallery) but no image_url
      // single-cover column — image_url is properties-only. Sending it on
      // a room patch returns PGRST204 ("Could not find the 'image_url'
      // column of 'rooms' in the schema cache"). The first entry of
      // images[] is the implicit cover; consumers (PropertyDetail Rooms
      // tab, RoomDetail sidebar) read images[0] via extractImageUrls().
      const translations = bilingualToTranslationsJsonb({
        en: form.descriptionEn,
        de: form.descriptionDe,
      });
      await updateMut.mutateAsync({
        id: room.id,
        propertyId: room.property_id,
        patch: {
          title: form.title.trim(),
          price: price.value,
          // Base deposit for the unit: the template new bookings start from and
          // the figure admin surfaces show as the base. Blank clears it, which
          // returns the room to the 2x-rent fallback (COL-360).
          deposit_amount:
            form.depositAmount.trim() === ""
              ? null
              : Number(form.depositAmount.replace(",", ".")),
          available: form.available,
          status: form.status === "" ? null : form.status,
          apartment_id:
            form.apartment_id === "" ? null : Number(form.apartment_id),
          size_sqm: sizeSqm.value,
          floor: form.floor.trim() || null,
          description: form.descriptionEn.trim() || null,
          description_translations: translations,
          images: form.images,
          keynest_partner_shop_name:
            form.keynest_partner_shop_name.trim() || null,
          keynest_partner_shop_address:
            form.keynest_partner_shop_address.trim() || null,
          // The per-room access link only applies to KeyNest pickup; clear it
          // when the effective method is manual so a stale link can't leak.
          keynest_access_url:
            effectiveMethod === "keynest"
              ? form.keynest_access_url.trim() || null
              : null,
          pickup_method:
            form.pickup_method === INHERIT ? null : form.pickup_method,
          pickup_instructions:
            isKeynestDefaultInstructions(instructionsValue) ||
            !instructionsValue.trim()
              ? null
              : instructionsValue.trim(),
          ...changedKeynestDetails(form, room),
        } as Partial<Room>,
      });

      const ibanTrim = form.bank_iban.trim();
      if (ibanTrim) {
        await bankUpsertMut.mutateAsync({
          room_id: room.id,
          holder: form.bank_holder.trim() || null,
          iban: ibanTrim.toUpperCase(),
          bic: form.bank_bic.trim().toUpperCase() || null,
          bank_name: form.bank_name.trim() || null,
        });
      } else if (bankOverride) {
        await bankDeleteMut.mutateAsync(room.id);
      }

      toast({
        title: t("admin.rooms.roomUpdated", "Room updated"),
        description: form.title,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t("admin.rooms.couldNotSaveChanges", "Could not save changes"),
        description: formatSupabaseError(err),
        variant: "destructive",
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-2xl overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        <SheetHeader className="space-y-1">
          <SheetTitle>{t("admin.room.edit", "Edit room")}</SheetTitle>
          <SheetDescription>
            {t(
              "admin.room.editDesc",
              "Updates the room directly. Press ⌘ ↵ to save."
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4">
          {/* Basics */}
          <Section title={t("admin.section.room", "Room")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label={t("admin.field.title", "Title")}
                required
                className="sm:col-span-2"
              >
                <Input
                  value={form.title}
                  onChange={(e) => patch({ title: e.target.value })}
                  placeholder={t(
                    "admin.rooms.roomNamePlaceholder",
                    "e.g. Bright corner room"
                  )}
                />
              </Field>
              <Field label={t("admin.field.rentMonthly", "Rent (€/mo)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) => patch({ price: e.target.value })}
                />
              </Field>
              <Field label={t("admin.field.depositBase", "Deposit (€)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={form.depositAmount}
                  onChange={(e) => patch({ depositAmount: e.target.value })}
                  placeholder={t(
                    "admin.field.depositBasePlaceholder",
                    "2x rent"
                  )}
                />
              </Field>
              <Field label={t("admin.field.status", "Status")}>
                <Select
                  value={form.status === "" ? undefined : form.status}
                  onValueChange={(v) => patch({ status: v as RoomStatus })}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "admin.field.statusPlaceholder",
                        "Select status…"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {ROOM_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("admin.field.size", "Size (m²)")}>
                <Input
                  type="number"
                  min={0}
                  step="0.1"
                  inputMode="decimal"
                  value={form.size_sqm}
                  onChange={(e) => patch({ size_sqm: e.target.value })}
                />
              </Field>
              <Field label={t("admin.field.floor", "Floor")}>
                <Input
                  value={form.floor}
                  onChange={(e) => patch({ floor: e.target.value })}
                  placeholder={inheritedFloor || "2nd"}
                />
                {inheritedFloor && (
                  <p className="mt-1 text-xs text-muted-foreground">
                    {language === "de"
                      ? `Leer lassen, um die Etage der Wohnung zu übernehmen (${inheritedFloor}).`
                      : `Leave blank to inherit the apartment's floor (${inheritedFloor}).`}
                  </p>
                )}
              </Field>
              {/* Derived, not entered. `available_from` is recomputed from the
                  confirmed move-out date on the room's bookings
                  (recompute_room_available_from), so anything typed here would
                  be overwritten the next time one of them changes. The manual
                  control is the Available toggle above. */}
              <Field label={t("admin.field.availableFrom", "Available from")}>
                <Input
                  type="date"
                  value={form.available_from}
                  readOnly
                  tabIndex={-1}
                  className="bg-muted/50 text-muted-foreground"
                />
              </Field>
              {apartments.length > 0 && (
                <Field
                  label={t("admin.field.apartment", "Apartment")}
                  className="sm:col-span-2"
                >
                  <Select
                    value={
                      form.apartment_id === "" ? "__none__" : form.apartment_id
                    }
                    onValueChange={(v) =>
                      patch({ apartment_id: v === "__none__" ? "" : v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "admin.field.apartmentPlaceholder",
                          "No apartment (resolve at property level)"
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        {t(
                          "admin.field.apartmentNone",
                          "No apartment (resolve at property level)"
                        )}
                      </SelectItem>
                      {apartments.map((a) => (
                        <SelectItem key={a.id} value={String(a.id)}>
                          {a.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">
                  {t(
                    "admin.field.availableForBooking",
                    "Available for booking"
                  )}
                </Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={form.available}
                    onCheckedChange={(v) => patch({ available: v })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {form.available
                      ? t("admin.field.bookable", "Yes — bookable")
                      : t(
                          "admin.field.notBookable",
                          "No — hidden from booking flow"
                        )}
                  </span>
                </div>
              </div>
              <Field
                label={t("admin.description", "Description")}
                className="sm:col-span-2"
              >
                <BilingualTextarea
                  valueEn={form.descriptionEn}
                  valueDe={form.descriptionDe}
                  onChange={({ en, de }) =>
                    patch({ descriptionEn: en, descriptionDe: de })
                  }
                  rows={3}
                  placeholder={t(
                    "admin.rooms.descriptionHighlightsPlaceholder",
                    "Highlights, view, furnishings…"
                  )}
                />
              </Field>
            </div>
          </Section>

          {/* Photos */}
          <Section title={t("admin.section.photos", "Photos")}>
            <ImageUploader
              label={t("admin.field.propertyPhotos", "Room photos")}
              value={form.images}
              onChange={(images) => patch({ images })}
              maxImages={15}
            />
          </Section>

          {/* Key pickup (KeyNest) — room-level override. Blank fields inherit
              the apartment's override, then the property default. The KeyNest
              access link is this room's own key and overrides any property
              fallback. Location/address reuse the legacy partner-shop columns. */}
          <Section
            title={t(
              "admin.section.keyPickupOverride",
              "Key pickup — override"
            )}
            description={t(
              "admin.section.keyPickupOverrideDesc",
              "Leave blank to inherit the apartment's pickup details, then the property default. Set these only when this room's keys are collected differently."
            )}
          >
            <div className="grid grid-cols-1 gap-3">
              <Field
                label={t("admin.field.pickupMethod", "Pickup method")}
                hint={t(
                  "admin.field.pickupMethodHint",
                  "KeyNest shows the access link + code guidance to the tenant. Manual shows the address and instructions only."
                )}
              >
                <Select
                  value={form.pickup_method}
                  onValueChange={(v) =>
                    patch({ pickup_method: v as FormState["pickup_method"] })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={INHERIT}>
                      {t("admin.field.pickupMethodInherit", "Inherit")} (
                      {PICKUP_METHOD_LABELS[parentMethod]})
                    </SelectItem>
                    {PICKUP_METHODS.map((m) => (
                      <SelectItem key={m} value={m}>
                        {PICKUP_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field
                label={t("admin.field.pickupLocation", "Pickup location")}
                hint={
                  effectiveMethod === "keynest"
                    ? t(
                        "admin.field.pickupLocationHintKeynest",
                        "The KeyNest / partner shop name, e.g. “Kiosk am Hauptbahnhof”."
                      )
                    : t(
                        "admin.field.pickupLocationHintManual",
                        "Name of the pickup point, e.g. “Building reception”."
                      )
                }
              >
                <Input
                  value={form.keynest_partner_shop_name}
                  onChange={(e) =>
                    patch({ keynest_partner_shop_name: e.target.value })
                  }
                  placeholder={
                    parentLocation
                      ? `${t("admin.field.inherits", "Inherits")}: ${parentLocation}`
                      : "e.g. Kiosk am Hauptbahnhof"
                  }
                />
              </Field>
              <Field
                label={t("admin.field.pickupAddress", "Pickup address")}
                hint={t(
                  "admin.field.pickupAddressHint",
                  "Street, house number, postal code, city of the pickup point."
                )}
              >
                <Textarea
                  rows={2}
                  value={form.keynest_partner_shop_address}
                  onChange={(e) =>
                    patch({ keynest_partner_shop_address: e.target.value })
                  }
                  placeholder={
                    parentAddress
                      ? `${t("admin.field.inherits", "Inherits")}: ${parentAddress}`
                      : "Bahnhofstr. 5, 80335 München"
                  }
                />
              </Field>
              <Field
                label={t(
                  "admin.field.pickupInstructions",
                  "Pickup instructions"
                )}
                hint={t(
                  "admin.field.pickupInstructionsHint",
                  "Optional. Shown to the tenant, e.g. “Ring the bell at reception during opening hours.”"
                )}
              >
                <Textarea
                  rows={2}
                  value={instructionsValue}
                  onChange={(e) => {
                    setInstructionsTouched(true);
                    patch({ pickup_instructions: e.target.value });
                  }}
                  placeholder={
                    parentInstructions
                      ? `${t("admin.field.inherits", "Inherits")}: ${parentInstructions}`
                      : "Ring the bell at reception during opening hours."
                  }
                />
              </Field>
              {effectiveMethod === "keynest" ? (
                <Field
                  label={t(
                    "admin.field.keynestAccessUrl",
                    "KeyNest access link"
                  )}
                  hint={t(
                    "admin.field.keynestAccessUrlHint",
                    "This room's own KeyNest link. Overrides the property fallback link."
                  )}
                >
                  <Input
                    type="url"
                    inputMode="url"
                    value={form.keynest_access_url}
                    onChange={(e) =>
                      patch({ keynest_access_url: e.target.value })
                    }
                    placeholder={parentAccessUrl || "https://keynest.com/…"}
                  />
                </Field>
              ) : null}
              {effectiveMethod === "keynest" ? (
                <>
                  <Field
                    label={t("admin.field.keynestPickupCode", "Pickup code")}
                    hint={t(
                      "admin.field.keynestPickupHint",
                      "Shown to tenants on the dashboard."
                    )}
                  >
                    <Input
                      className="font-mono"
                      value={form.keynest_pickup_code}
                      onChange={(e) =>
                        patch({ keynest_pickup_code: e.target.value })
                      }
                      placeholder="KN-…"
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={t(
                        "admin.field.keynestOpeningHoursWeekdays",
                        "Opening hours (weekdays)"
                      )}
                    >
                      <Input
                        value={form.keynest_opening_hours_weekdays}
                        onChange={(e) =>
                          patch({
                            keynest_opening_hours_weekdays: e.target.value,
                          })
                        }
                        placeholder="Mon-Fri 9-18"
                      />
                    </Field>
                    <Field
                      label={t(
                        "admin.field.keynestOpeningHoursSaturday",
                        "Opening hours (Saturday)"
                      )}
                    >
                      <Input
                        value={form.keynest_opening_hours_saturday}
                        onChange={(e) =>
                          patch({
                            keynest_opening_hours_saturday: e.target.value,
                          })
                        }
                        placeholder="Sat 10-14"
                      />
                    </Field>
                  </div>
                  <Field
                    label={t("admin.field.keynestKeyLabel", "Key label")}
                    hint={t(
                      "admin.field.keynestKeyLabelHint",
                      "Optional, e.g. apartment number."
                    )}
                  >
                    <Input
                      value={form.keynest_key_label}
                      onChange={(e) =>
                        patch({ keynest_key_label: e.target.value })
                      }
                    />
                  </Field>
                </>
              ) : null}
              {effectiveMethod === "manual" ? (
                <Field
                  label={t("admin.field.keynestAccessCode", "Access code")}
                  hint={t(
                    "admin.field.keynestAccessHint",
                    "Door / locker code, optional."
                  )}
                >
                  <Input
                    className="font-mono"
                    value={form.keynest_access_code}
                    onChange={(e) =>
                      patch({ keynest_access_code: e.target.value })
                    }
                  />
                </Field>
              ) : null}
            </div>
          </Section>

          {/* Bank override */}
          <Section
            title={t("admin.section.bankOverride", "Bank override (rare)")}
            description={t(
              "admin.section.bankOverrideDesc",
              "Set this only if rent for THIS room flows to a different account than the rest of the property. Leave blank to inherit."
            )}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label={t("admin.field.bankHolder", "Account holder")}>
                <Input
                  value={form.bank_holder}
                  onChange={(e) => patch({ bank_holder: e.target.value })}
                />
              </Field>
              <Field label={t("admin.field.bankIban", "IBAN")}>
                <Input
                  className="font-mono uppercase"
                  spellCheck={false}
                  autoComplete="off"
                  value={form.bank_iban}
                  onChange={(e) => patch({ bank_iban: e.target.value })}
                />
              </Field>
              <Field label={t("admin.field.bankBic", "BIC")}>
                <Input
                  className="font-mono uppercase"
                  spellCheck={false}
                  autoComplete="off"
                  value={form.bank_bic}
                  onChange={(e) => patch({ bank_bic: e.target.value })}
                />
              </Field>
              <Field label={t("admin.field.bankName", "Bank name")}>
                <Input
                  value={form.bank_name}
                  onChange={(e) => patch({ bank_name: e.target.value })}
                />
              </Field>
            </div>
          </Section>
        </div>

        <SheetFooter className="mt-6 gap-2 sticky bottom-0 bg-background pt-4 border-t">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t("admin.action.cancel", "Cancel")}
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("admin.action.saving", "Saving…")}
              </>
            ) : (
              t("admin.action.saveChanges", "Save changes")
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
